import { AlertTriangle, CheckCircle, CloudSun, Navigation2, Signpost } from 'lucide-react';
import { useRoutePoints } from '../hooks/useRoutePoints';
import { useRouteTrafficNews } from '../hooks/useRouteTrafficNews';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, weatherService } from '../services/api';
import { useQuery } from '@tanstack/react-query';

const SEVERITY_STYLE: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
  unknown: 'bg-gray-100 text-gray-500',
};

const CORRIDOR_MILES = 5;

/**
 * Bottom-of-page traffic + weather widget for a job's live route. Shown only while the job's
 * map is in "live" mode (LiveTrackingMap for InProgress/Paused, or a live=true RouteMap for
 * pre-start Same Day Delivery) — the caller decides when to render this.
 *
 * Traffic is filtered along the actual route corridor (not just a point radius) via the
 * TrafficNewsController route endpoint; weather is a single point sample at `weatherLat/Lng`
 * (the caller passes carrier GPS when tracking is live, or pickup coordinates before the
 * carrier has started).
 */
export function JobLiveTrafficWeather({
  pickupLat, pickupLng, deliveryLat, deliveryLng, weatherLat, weatherLng,
}: {
  pickupLat: number; pickupLng: number;
  deliveryLat: number; deliveryLng: number;
  weatherLat: number; weatherLng: number;
}) {
  const { getToken } = useAppAuth();

  const { data: routePoints = [] } = useRoutePoints(pickupLat, pickupLng, deliveryLat, deliveryLng);
  const { data: incidents = [], isLoading: trafficLoading, error: trafficError } =
    useRouteTrafficNews(routePoints, CORRIDOR_MILES, routePoints.length > 0);

  const { data: hours = [], isLoading: weatherLoading, error: weatherError } = useQuery({
    queryKey: ['job-weather', weatherLat, weatherLng],
    queryFn: async () => {
      const client = createApiClient(getToken);
      return weatherService(client).getHourly(weatherLat, weatherLng);
    },
    staleTime: 15 * 60_000,
  });

  return (
    <div data-testid="job-traffic-weather-section" className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-gray-700" />
          <span className="font-medium text-gray-900 text-sm">Traffic on Route</span>
        </div>

        {trafficLoading && <p className="p-4 text-sm text-gray-400">Loading traffic news…</p>}

        {!trafficLoading && !!trafficError && (
          <p data-testid="job-traffic-error" className="p-4 text-sm text-red-500">
            Couldn't load traffic news. Refresh to try again.
          </p>
        )}

        {!trafficLoading && !trafficError && incidents.length === 0 && (
          <div className="py-6 text-center">
            <CheckCircle className="w-6 h-6 text-green-300 mx-auto mb-1.5" />
            <p className="text-sm text-gray-400">No traffic incidents on this route</p>
          </div>
        )}

        {!trafficLoading && !trafficError && incidents.length > 0 && (
          <ul data-testid="job-traffic-list" className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {incidents.map(incident => {
              const sev = SEVERITY_STYLE[incident.severity] ?? SEVERITY_STYLE.unknown;
              return (
                <li key={incident.id} data-testid="job-traffic-item" className="p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${sev}`}>{incident.severity}</span>
                    {incident.roadName && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Signpost className="w-3 h-3 text-gray-400 shrink-0" /> {incident.roadName}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800">{incident.title}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Navigation2 className="w-3 h-3 shrink-0" /> {incident.distanceMiles.toFixed(1)} miles from route
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <CloudSun className="w-4 h-4 text-gray-700" />
          <span className="font-medium text-gray-900 text-sm">Weather at Location</span>
        </div>

        <div className="p-4">
          {weatherLoading && <p className="text-sm text-gray-400">Loading forecast…</p>}

          {!weatherLoading && !!weatherError && (
            <p data-testid="job-weather-error" className="text-sm text-red-500">
              Couldn't load the forecast. Refresh to try again.
            </p>
          )}

          {!weatherLoading && !weatherError && hours.length === 0 && (
            <p className="text-sm text-gray-400">No forecast available right now.</p>
          )}

          {!weatherLoading && !weatherError && hours.length > 0 && (
            <div data-testid="job-weather-hourly-scroll" className="flex gap-3 overflow-x-auto pb-1">
              {hours.slice(0, 8).map((hour, i) => (
                <div key={hour.time} data-testid={`job-weather-hour-${i}`} className="flex flex-col items-center w-14 shrink-0">
                  <span className="text-xs text-gray-500">
                    {new Date(hour.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                  <span className="text-2xl my-1">{hour.icon}</span>
                  <span className="text-sm font-bold text-gray-900">{hour.temperatureC}°</span>
                  <span className="text-xs text-blue-400 mt-0.5">{hour.precipitationProbability}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
